import { useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, addDays, format } from "date-fns";

export function useNotificationGenerator(userId: string | undefined, onComplete?: () => void) {
  const hasRun = useRef(false);

  useEffect(() => {
    if (!userId || hasRun.current) return;
    hasRun.current = true;

    generate(userId).then(() => onComplete?.());
    // MS143: onComplete added to deps. The hasRun.current guard
    // means re-runs are no-ops anyway (it short-circuits before
    // any work), so adding the dep is behaviour-preserving and
    // eliminates the eslint missing-deps warning. The CLOSURE
    // semantic is unchanged: whatever onComplete reference was
    // captured when generate started is what gets called on
    // resolution — that's a separate concern from the effect's
    // dependency tracking, and would need a ref-pattern refactor
    // if we wanted "always call latest" semantics.
  }, [userId, onComplete]);
}

async function generate(userId: string) {
  // Welcome notification is now created during onboarding, skip here

  // 2. Get company
  const { data: profile } = await supabase
    .from("profiles")
    .select("company_id")
    .eq("id", userId)
    .single();
  if (!profile?.company_id) return;

  const today = startOfDay(new Date());
  const weekOut = addDays(today, 7);

  // 3. Fetch upcoming celebrations
  const { data: celebrations, error: celError } = await supabase
    .from("celebrations")
    .select("id, date, type, team_members(name)")
    .eq("company_id", profile.company_id)
    .gte("date", format(today, "yyyy-MM-dd"))
    .lte("date", format(weekOut, "yyyy-MM-dd"))
    .in("status", ["upcoming", "approved"]);

  if (celError) console.error("Failed to load celebrations for notifications:", celError);
  if (!celebrations?.length) return;

  // 4. Get existing notification related_ids
  const { data: existing } = await supabase
    .from("notifications")
    .select("related_id")
    .eq("user_id", userId)
    .eq("type", "celebration");

  const existingIds = new Set((existing || []).map((n) => n.related_id));

  // 5. Insert missing notifications
  for (const cel of celebrations) {
    if (existingIds.has(cel.id)) continue;
    const memberData = cel.team_members;
    const name = (Array.isArray(memberData) ? memberData[0]?.name : memberData?.name) || "Team member";
    // MS439: cel.date is a date-only Postgres column. Without "T00:00:00"
    // the constructor uses UTC midnight → daysUntil is off by one in AEST.
    const celDate = new Date(cel.date + "T00:00:00");
    const daysUntil = Math.round((celDate.getTime() - today.getTime()) / 86400000);
    // MS103: was `cel.type === "birthday" ? "birthday" : "work anniversary"`
    // which silently mislabeled every welcome / farewell / milestone /
    // other as "work anniversary" — so a welcome celebration generated
    // a notification titled "Upcoming work anniversary: Sarah".
    const typeLabel = labelForType(cel.type);

    let title: string;
    let message: string;
    if (daysUntil <= 1) {
      title = `Tomorrow: ${name}'s ${typeLabel}`;
      message = `Don't forget, ${name}'s ${typeLabel} is tomorrow.`;
    } else {
      title = `Upcoming ${typeLabel}: ${name}`;
      message = `${name}'s ${typeLabel} is on ${format(celDate, "d MMM")}. Approve or skip this celebration.`;
    }

    const { error: insertError } = await supabase.from("notifications").insert({
      user_id: userId,
      title,
      message,
      type: "celebration",
      related_id: cel.id,
      related_type: "celebration",
    });
    if (insertError) {
      console.error("Failed to insert notification:", insertError);
    }
    // Note: the 3-day "celebration_reminder" email is fired server-side
    // by the `scheduled-celebration-notifications` edge function (see
    // migration 20260421000000). The previous client-side trigger was
    // unreliable, it only fired when an admin happened to open the app
    // on the 3-day-prior date. The scheduled function runs daily and is
    // idempotent.
  }
}

// MS103: shared celebration-type → human label mapping. Mirrors the
// occasion vocabulary used across Calendar's humanType helper +
// chat-concierge's submit_brief enum so the user sees consistent
// labels everywhere a celebration is named.
function labelForType(type: string): string {
  switch (type) {
    case "birthday":
      return "birthday";
    case "work_anniversary":
      return "work anniversary";
    case "welcome":
      return "first day";
    case "farewell":
      return "farewell";
    case "milestone":
      return "milestone";
    // Unknown types fall back to a neutral "celebration" — better than
    // mislabeling as "work anniversary" the way pre-MS103 did.
    default:
      return "celebration";
  }
}
